import { Box, Chip, TableCell, TableRow, Grid } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import { Alert } from 'components/common/Alert';
import TableLoader from 'components/common/TableLoader';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableTabsComponent from 'components/common/TableTabsComponent';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import DensitySelect from 'components/common/simpleSelectors/Density';
import SupplierSelect from 'components/common/simpleSelectors/Supplier';
import ProductSizeSelect from 'components/common/simpleSelectors/ProductSize';
import CapsuleTypeSelect from 'components/common/simpleSelectors/CapsuleType';
// constants, reducers, graphql, styles
import { textWhiteSpace } from 'styles/commonComponentStyle';
import { Action, ActionType, State, initialState, capsulePriceReducer } from 'reducer/capsulePriceReducer';
import {
  TABS,
  TabEnum,
  HTTP_STATUS,
  ACTIVE_TEXT,
  DENSITY_TEXT,
  INACTIVE_TEXT,
  SUPPLIER_TEXT,
  PRODUCT_SIZE_TEXT,
  CAPSULE_TYPE_TEXT,
  LOADING_TABLE_ROWS,
  EDIT_CAPSULE_PRICE_ROUTE,
  CAPSULE_PRICE_TABLE_HEADER,
} from 'constants/index';
import {
  ProductTypes,
  CapsulePricesPayload,
  useUpdateCapsulePriceMutation,
  useFindAllCapsulePricesTableLazyQuery,
} from 'generated/graphql';

const CapsulePriceTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(capsulePriceReducer, initialState);
  const { page, rowsPerPage, count, data, activeTab, density, supplier, capsuleType, productSize } = state;

  const { value: densityId } = density || {};
  const { value: supplierId } = supplier || {};
  const { value: capsuleTypeId } = capsuleType || {};
  const { value: productSizeId } = productSize || {};

  const [findAllCapsulePrices, { loading: fetchLoading, error }] = useFindAllCapsulePricesTableLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllCapsulePrices } = data;
      const { pagination, response, data: capsulePriceData } = findAllCapsulePrices || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({
          type: ActionType.SET_DATA,
          data: capsulePriceData as CapsulePricesPayload['data'],
        });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateCapsulePriceStatus, { loading: updateLoading }] = useUpdateCapsulePriceMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateCapsulePrice } = data;
      const { response } = updateCapsulePrice || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchCapsulePrices();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchCapsulePrices = useCallback(async () => {
    await findAllCapsulePrices({
      variables: {
        findAllCapsulePricesInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          ...(densityId && { densityId }),
          ...(supplierId && { supplierId }),
          ...(capsuleTypeId && { capsuleTypeId }),
          ...(productSizeId && { productSizeId }),
          ...(activeTab !== TabEnum.All && { isActive: activeTab === TabEnum.Active }),
        },
      },
    });
  }, [
    findAllCapsulePrices,
    rowsPerPage,
    page,
    activeTab,
    densityId,
    supplierId,
    capsuleTypeId,
    productSizeId,
  ]);

  useEffect(() => {
    fetchCapsulePrices();
  }, [fetchCapsulePrices]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as TabEnum });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateCapsulePriceStatus({
      variables: {
        updateCapsulePriceInput: {
          id,
          isActive: !status,
        },
      },
    });
  };

  const loading = fetchLoading || updateLoading;
  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={TABS} tabHandler={tabHandler} />

      <Box m={({ spacing }) => spacing(2, 3)}>
        <Grid container spacing={2}>
          <Grid item md={6} sm={12} xs={12}>
            <CapsuleTypeSelect
              isClearable
              value={capsuleType}
              name="capsule-type"
              title={CAPSULE_TYPE_TEXT}
              handleChange={(item) => dispatch({ type: ActionType.SET_CAPSULE_TYPE, capsuleType: item })}
            />
          </Grid>

          <Grid item md={6} sm={12} xs={12}>
            <ProductSizeSelect
              isClearable
              value={productSize}
              productType={ProductTypes.Capsule}
              name="product-size"
              title={PRODUCT_SIZE_TEXT}
              handleChange={(item) => dispatch({ type: ActionType.SET_PRODUCT_SIZE, productSize: item })}
            />
          </Grid>

          <Grid item md={6} sm={12} xs={12}>
            <DensitySelect
              isClearable
              value={density}
              name="density"
              title={DENSITY_TEXT}
              handleChange={(item) => dispatch({ type: ActionType.SET_DENSITY, density: item })}
            />
          </Grid>

          <Grid item md={6} sm={12} xs={12}>
            <SupplierSelect
              isClearable
              value={supplier}
              name="supplier"
              title={SUPPLIER_TEXT}
              handleChange={(item) => dispatch({ type: ActionType.SET_SUPPLIER, supplier: item })}
            />
          </Grid>
        </Grid>
      </Box>

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={CAPSULE_PRICE_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={8} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const { id, price, isActive, capacity, capsuleType, supplier, productSize, density } =
                cell || {};

              const { name: densityName } = density || {};
              const { name: supplierName } = supplier || {};
              const { name: productSizeName } = productSize || {};
              const { name: capsuleTypeName } = capsuleType || {};

              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{capsuleTypeName ?? '--'}</TableCell>
                  <TableCell>{productSizeName ?? '--'}</TableCell>
                  <TableCell>{densityName ?? '--'}</TableCell>
                  <TableCell>{supplierName ?? '--'}</TableCell>
                  <TableCell>{capacity ?? '--'}</TableCell>
                  <TableCell>{price ? `$${price}` : '--'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                      color={isActive ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={!!isActive}
                      editRoute={EDIT_CAPSULE_PRICE_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default CapsulePriceTable;
